"use client";

import React from "react";
import { ArrowRight, Microscope, Wrench, CircleCheck as CheckCircle2 } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  subtitle: string;
  description: string;
  href: string;
  badge: string;
  iconName: "microscope" | "wrench";
  bullet1: string;
  bullet2: string;
  bullet3: string;
  ctaText: string;
}
const servicesList: ServiceItem[] = [{
  id: "consultation",
  title: "Application Consultation",
  subtitle: "Method Development & Feasibility",
  description: "Work directly with analytical specialists to qualify spectroscopic methods, evaluate sample matrices, and optimize measurement setups for regulated lab workflows.",
  href: "/ApplicationConsultationPage",
  badge: "Specialist Advisory",
  iconName: "microscope",
  bullet1: "Sample feasibility and spectral validation",
  bullet2: "Regulatory compliance and qualification roadmap",
  bullet3: "Technology selection for PAT and laboratory use",
  ctaText: "Explore Consultation Services"
}, {
  id: "maintenance",
  title: "Preventive Maintenance & Repairs",
  subtitle: "Instrument Uptime & Lifecycle Care",
  description: "Scheduled servicing, certified optical recalibration, and rapid emergency repairs to keep your spectroscopy and testing instruments operating within specification.",
  href: "/PreventiveMaintenanceAndRepairsPage",
  badge: "Lifecycle & Service",
  iconName: "wrench",
  bullet1: "Annual PM protocols and optical alignment",
  bullet2: "Emergency on-site and depot repair options",
  bullet3: "Traceable calibration and service records",
  ctaText: "View Maintenance Plans"
}];
export default function RelatedServices() {
  const prefersReducedMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        delay: prefersReducedMotion ? 0 : i * 0.12,
        ease: "easeOut"
      }
    })
  };
  return <section id="relatedservices" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-t border-border">
      <div className="page-container">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-secondary text-secondary-foreground text-xs font-semibold tracking-wide uppercase mb-3">
              Support Capabilities
            </div>
            <h2 className="font-default text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
              Related Analytical Services
            </h2>
            <p className="mt-3 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
              Complement your instrumentation with expert consultation and ongoing maintenance coverage designed for demanding laboratory environments.
            </p>
          </div>

          <div className="hidden md:block shrink-0">
            <Link to="/ServicesPage">
              <Button variant="outline" className="gap-2">
                All Services
                <ArrowRight className="size-4" />
              </Button>
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
          {servicesList.map((service, i) => {
          const isMicroscope = service.iconName === "microscope";
          return <motion.div key={service.id} data-index={i} custom={i} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }} variants={cardVariants} className="h-full flex">
                <Card className="border border-border/80 bg-card hover:border-primary/40 shadow-xs hover:shadow-md transition-[border-color,box-shadow] duration-200 flex-1 flex flex-col justify-between overflow-hidden">
                  <div>
                    <CardHeader className="space-y-4 pb-4">
                      <div className="flex items-center justify-between gap-3">
                        <div className="size-12 rounded-lg bg-secondary text-secondary-foreground flex items-center justify-center">
                          {isMicroscope ? <Microscope className="size-6 text-primary" /> : <Wrench className="size-6 text-primary" />}
                        </div>
                        <Badge variant="secondary" className="font-medium">
                          {service.badge}
                        </Badge>
                      </div>

                      <div>
                        <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                          {service.subtitle}
                        </span>
                        <CardTitle className="text-2xl font-bold text-card-foreground mt-1">
                          {service.title}
                        </CardTitle>
                      </div>

                      <CardDescription className="text-base text-muted-foreground leading-normal">
                        {service.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-0 pb-6">
                      <div className="border-t border-border/60 pt-4 mt-2">
                        <p className="text-xs font-semibold uppercase tracking-wider text-card-foreground mb-3">
                          Service Inclusions
                        </p>
                        <ul className="space-y-2.5 text-sm text-muted-foreground font-sans">
                          <li className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{service.bullet1}</span>
                          </li>
                          <li className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{service.bullet2}</span>
                          </li>
                          <li className="flex items-start gap-2.5">
                            <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                            <span>{service.bullet3}</span>
                          </li>
                        </ul>
                      </div>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-2 pb-6 border-t border-border/60 bg-muted/20">
                    <Link to={service.href} className="w-full">
                      <Button className="w-full justify-between group" size="lg">
                        <span>{service.ctaText}</span>
                        <ArrowRight className="size-4 group-hover:translate-x-1 transition-transform" />
                      </Button>
                    </Link>
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </div>

        <div className="mt-8 text-center md:hidden">
          <Link to="/ServicesPage" className="inline-block w-full">
            <Button variant="outline" className="w-full justify-center gap-2">
              All Services
              <ArrowRight className="size-4" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}